import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';
import { SafeImage } from '@/components/SafeImage';
import { AuthorAvatar } from '@/components/AuthorAvatar';
import { getAuthor } from '@/data/authors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Clock, ChevronLeft, ChevronRight, AlertCircle, Search, X, Star, SlidersHorizontal, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 6;

const categoryColors = {
  'App Development': 'bg-brand-blue/10 text-brand-blue',
  'AI & Engineering': 'bg-brand-orange/10 text-brand-orange',
  'Web Development': 'bg-purple-100 text-purple-700',
  'Enterprise Strategy': 'bg-emerald-100 text-emerald-700',
  'Growth & Ops': 'bg-amber-100 text-amber-700',
  'Architecture': 'bg-cyan-100 text-cyan-700',
  'Engineering': 'bg-slate-200 text-slate-700',
  'Artificial Intelligence': 'bg-indigo-100 text-indigo-700',
};

const chipId = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/* ── Standard post card ── */
const PostCard = ({ post, index }) => {
  const catColor = categoryColors[post.category] || 'bg-secondary text-foreground';
  const author = getAuthor(post.author);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: Math.min(index * 0.06, 0.4) }}
      whileHover={{ y: -3 }}
      data-testid={`blog-card-${post.slug}`}
    >
      <Link
        to={`/blog/${post.slug}`}
        className="group block bg-white rounded-2xl border border-[color:var(--hairline)] hover:border-brand-blue/25 transition-shadow transition-colors duration-200 hover:shadow-[var(--shadow-lift)] overflow-hidden h-full"
      >
        {/* Cover image or gradient fallback */}
        <div className="h-44 sm:h-52 overflow-hidden relative">
          {post.coverImage ? (
            <SafeImage
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              data-testid={`blog-card-image-${post.slug}`}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-blue/8 via-brand-orange/5 to-brand-blue/10 flex items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm border border-[color:var(--hairline)]">
                <span className="text-2xl font-extrabold text-brand-blue">{post.title.charAt(0)}</span>
              </div>
            </div>
          )}
          <span className={`absolute top-4 left-4 px-2.5 py-1 rounded-lg text-[11px] font-semibold backdrop-blur-sm ${post.coverImage ? 'bg-white/90 border border-white/20' : ''} ${catColor}`}>
            {post.category}
          </span>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-foreground mb-2 leading-snug group-hover:text-brand-blue transition-colors duration-200 line-clamp-2">
            {post.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between pt-3 border-t border-[color:var(--hairline)]">
            <div className="flex items-center gap-2 min-w-0">
              <AuthorAvatar author={author} size="xs" />
              <div className="min-w-0 leading-tight">
                <span className="block text-xs font-semibold text-foreground truncate">{author.name}</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Clock className="w-3 h-3" /> {post.readTime}</span>
              </div>
            </div>
            <span className="text-xs font-bold text-brand-orange group-hover:text-brand-orange-dark transition-colors inline-flex items-center gap-1 shrink-0">
              Read <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ── Large featured hero (newest C-suite piece) ── */
const FeaturedHero = ({ post }) => {
  const catColor = categoryColors[post.category] || 'bg-secondary text-foreground';
  const author = getAuthor(post.author);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="mb-10 sm:mb-12"
      data-testid="blog-featured"
    >
      <Link
        to={`/blog/${post.slug}`}
        className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl border border-[color:var(--hairline)] overflow-hidden hover:border-brand-blue/25 hover:shadow-[var(--shadow-lift)] transition-shadow transition-colors duration-200"
        data-testid={`blog-featured-card-${post.slug}`}
      >
        {/* Cover */}
        <div className="relative h-60 sm:h-72 lg:h-auto lg:min-h-[360px] overflow-hidden">
          {post.coverImage ? (
            <SafeImage
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              data-testid={`blog-featured-image-${post.slug}`}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-blue/10 to-brand-orange/5" />
          )}
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-brand-orange text-white shadow-[var(--shadow-soft)]">
            <Star className="w-3.5 h-3.5 fill-current" /> Featured
          </span>
        </div>

        {/* Copy */}
        <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
          <span className={`self-start px-2.5 py-1 rounded-lg text-[11px] font-semibold mb-4 ${catColor}`}>
            {post.category}
          </span>
          <h2 className="text-xl sm:text-2xl lg:text-[1.75rem] font-extrabold tracking-tight text-foreground leading-tight group-hover:text-brand-blue transition-colors duration-200">
            {post.title}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <AuthorAvatar author={author} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{author.name}</p>
              <p className="text-xs text-muted-foreground truncate">{author.role} · {post.readTime}</p>
            </div>
            <span className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange group-hover:text-brand-orange-dark transition-colors whitespace-nowrap">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const CardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-[color:var(--hairline)] overflow-hidden" data-testid="blog-card-skeleton">
    <div className="h-44 sm:h-52 bg-slate-100 animate-pulse" />
    <div className="p-5 sm:p-6">
      <div className="h-4 bg-slate-100 rounded animate-pulse mb-3 w-3/4" />
      <div className="h-3 bg-slate-100 rounded animate-pulse mb-2" />
      <div className="h-3 bg-slate-100 rounded animate-pulse w-5/6" />
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-[color:var(--hairline)]">
        <div className="h-6 w-24 bg-slate-100 rounded-full animate-pulse" />
        <div className="h-3 w-10 bg-slate-100 rounded animate-pulse" />
      </div>
    </div>
  </div>
);

/* Build the list of page tokens, collapsing long ranges with ellipsis. */
function getPageItems(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  if (current > 3) items.push('ellipsis-start');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) items.push(i);
  if (current < total - 2) items.push('ellipsis-end');
  items.push(total);
  return items;
}

/* ── Premium, minimal pagination bar ── */
const Pagination = ({ currentPage, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const items = getPageItems(currentPage, totalPages);
  const btnBase = 'inline-flex items-center justify-center h-10 min-w-[2.5rem] px-3 rounded-xl text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/20';

  return (
    <nav className="mt-12 sm:mt-14 flex items-center justify-center gap-1.5" aria-label="Blog pagination" data-testid="blog-pagination">
      <button
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${btnBase} gap-1 border border-[color:var(--hairline)] text-foreground hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        data-testid="pagination-prev"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Previous</span>
      </button>

      <div className="flex items-center gap-1">
        {items.map((it) =>
          typeof it === 'string' ? (
            <span key={it} className="inline-flex items-center justify-center h-10 w-8 text-muted-foreground select-none" data-testid="pagination-ellipsis">…</span>
          ) : (
            <button
              key={it}
              onClick={() => onChange(it)}
              aria-current={it === currentPage ? 'page' : undefined}
              className={`${btnBase} ${
                it === currentPage
                  ? 'bg-brand-blue text-white shadow-[var(--shadow-soft)]'
                  : 'text-foreground hover:bg-secondary'
              }`}
              data-testid={`pagination-page-${it}`}
            >
              {it}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${btnBase} gap-1 border border-[color:var(--hairline)] text-foreground hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        data-testid="pagination-next"
        aria-label="Next page"
      >
        <span className="hidden sm:inline">Next</span> <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default function BlogCatalog() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(''); // debounced query actually sent
  const [sort, setSort] = useState('newest');
  const [featured, setFeatured] = useState(null);

  const gridRef = useRef(null);
  const didMount = useRef(false);

  const isFiltering = Boolean(category) || Boolean(search);
  const showFeatured = Boolean(featured) && !isFiltering && page === 1;

  /* Fetch category list + featured post once on mount. */
  useEffect(() => {
    axios.get(`${API}/blog/categories`)
      .then((res) => setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []))
      .catch(() => setCategories([]));
    axios.get(`${API}/blog/featured`)
      .then((res) => setFeatured(res.data || null))
      .catch(() => setFeatured(null));
  }, []);

  /* Debounce the search box → search query. */
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* Any filter/sort change returns to page 1. */
  useEffect(() => { setPage(1); }, [category, search, sort]);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    setError(false);
    const params = { page, limit: PAGE_SIZE };
    if (category) params.category = category;
    if (search) params.q = search;
    if (sort && sort !== 'newest') params.sort = sort;
    return axios
      .get(`${API}/blog`, { params })
      .then((res) => {
        const d = res.data || {};
        setPosts(Array.isArray(d.posts) ? d.posts : []);
        setTotalPages(d.totalPages || 1);
        setTotalPosts(d.totalPosts || 0);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [page, category, search, sort]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Smoothly scroll to the top of the grid whenever the page changes (skip first mount).
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (gridRef.current) {
      const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, [page]);

  const handlePageChange = useCallback((next) => {
    if (next < 1 || next > totalPages || next === page) return;
    setPage(next);
  }, [page, totalPages]);

  const clearFilters = useCallback(() => {
    setCategory('');
    setSearchInput('');
    setSearch('');
  }, []);

  const chipClass = (active) =>
    `inline-flex items-center h-9 px-3.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/20 ${
      active
        ? 'bg-brand-blue text-white border-brand-blue shadow-[var(--shadow-soft)]'
        : 'bg-white text-foreground border-[color:var(--hairline)] hover:bg-secondary hover:border-brand-blue/25'
    }`;

  return (
    <div className="min-h-screen bg-background" data-testid="blog-catalog-page">
      <SEOHead title="MediaLabs Insights | Blog" description="Thoughts on AI, software development, and shipping products fast." path="/blog" />
      <div className="pt-32 sm:pt-36 lg:pt-40 pb-12 sm:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center mb-10 sm:mb-12"
          >
            <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">Blog</span>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              MediaLabs Insights
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
              Thoughts on AI, software development, and shipping products fast.
            </p>
          </motion.div>

          {/* Featured hero (default, page 1 only) */}
          {showFeatured && <FeaturedHero post={featured} />}

          {/* Toolbar: search + category chips */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="mb-8 sm:mb-10"
            data-testid="blog-toolbar"
          >
            {/* Search + sort */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative w-full sm:max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles…"
                  aria-label="Search articles"
                  className="w-full h-11 pl-10 pr-10 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/20 focus-visible:border-brand-blue/40 transition-colors"
                  data-testid="blog-search-input"
                />
                {searchInput && (
                  <button
                    onClick={() => setSearchInput('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                    data-testid="blog-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Sort control */}
              <div className="sm:ml-auto flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium text-muted-foreground hidden sm:inline">Sort by</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger
                    className="h-11 w-full sm:w-[168px] rounded-xl border-[color:var(--hairline)] bg-white text-sm font-semibold text-foreground focus:ring-4 focus:ring-brand-blue/20"
                    aria-label="Sort articles"
                    data-testid="blog-sort-trigger"
                  >
                    <ArrowUpDown className="w-4 h-4 text-muted-foreground mr-1.5" />
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent data-testid="blog-sort-content">
                    <SelectItem value="newest" data-testid="blog-sort-newest">Newest</SelectItem>
                    <SelectItem value="quickest" data-testid="blog-sort-quickest">Quickest reads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="blog-category-chips">
              <button
                onClick={() => setCategory('')}
                className={chipClass(category === '')}
                aria-pressed={category === ''}
                data-testid="category-chip-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setCategory((prev) => (prev === c.name ? '' : c.name))}
                  className={chipClass(category === c.name)}
                  aria-pressed={category === c.name}
                  data-testid={`category-chip-${chipId(c.name)}`}
                >
                  {c.name}
                  <span className={`ml-1.5 text-[11px] ${category === c.name ? 'text-white/70' : 'text-muted-foreground'}`}>{c.count}</span>
                </button>
              ))}
            </div>

            {/* Active-filter summary */}
            {isFiltering && !loading && !error && (
              <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="blog-filter-summary">
                <SlidersHorizontal className="w-4 h-4 text-brand-blue" />
                <span>
                  <span className="font-semibold text-foreground">{totalPosts}</span> {totalPosts === 1 ? 'article' : 'articles'}
                  {category && <> in <span className="font-semibold text-foreground">{category}</span></>}
                  {search && <> for “<span className="font-semibold text-foreground">{search}</span>”</>}
                </span>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-brand-blue font-semibold hover:underline"
                  data-testid="blog-clear-filters"
                >
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
            )}
          </motion.div>

          {/* Grid anchor for scroll-to-top on page change */}
          <div ref={gridRef} className="scroll-mt-32">
            {error ? (
              <div className="text-center py-20" data-testid="blog-error-state">
                <AlertCircle className="w-10 h-10 text-brand-orange mx-auto mb-3" />
                <p className="text-base font-semibold text-foreground mb-1">We couldn't load the articles</p>
                <p className="text-sm text-muted-foreground mb-5">Please check your connection and try again.</p>
                <button
                  onClick={fetchPosts}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue/90 transition-colors"
                  data-testid="blog-retry"
                >
                  Try Again
                </button>
              </div>
            ) : loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="blog-loading">
                {Array.from({ length: PAGE_SIZE }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20" data-testid="blog-empty-state">
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                  <Search className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-base font-semibold text-foreground mb-1">No articles found</p>
                <p className="text-sm text-muted-foreground mb-5">
                  {isFiltering ? 'Try a different keyword or category.' : 'Check back soon for new insights.'}
                </p>
                {isFiltering && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue/90 transition-colors"
                    data-testid="blog-empty-clear"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="blog-grid">
                {posts.map((post, i) => (
                  <PostCard key={post.slug} post={post} index={i} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && !error && posts.length > 0 && (
            <>
              <Pagination currentPage={page} totalPages={totalPages} onChange={handlePageChange} />
              {totalPages > 1 && (
                <p className="mt-5 text-center text-xs text-muted-foreground" data-testid="blog-page-meta">
                  Page {page} of {totalPages}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
